/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Col, Row } from 'react-bootstrap';
import { isEqual, get } from 'lodash';

import ActorItem from '../../components/SectionedItems/ActorItem';
import ComponentHeader from '../../components/Headers/component-header';
import InlineQuery from '../../components/InlineQuery';
import SectionedItems from '../../components/SectionedItems';
import ModuleExport from '../../components/ModuleExport';
import { FilterTypes } from '../../components/Filters';
// import { getEmotionVotes } from '../../utils/movie';
import moviesConfig from '../configs/movies-config';
import { movieReportEngagement } from '../../config/filters';
import { extractFilterValues } from '../../utils/filters';
import DivergingBar from '../../components/Charts/EmotionDivergingBar';
import {
  formatEmotionData,
  emotionsAxisElement,
  getEmotionVotes
} from '../../utils/emotions';

import { formatActorsFromQuery } from './table-config';

import Reactions from '../../components/Reactions';

import './styles/MovieEngagement.css';

const ReactionsQuery = {
  id: 'movieReactions',
  meta: {
    fields: [
      'movie',
      'characters',
      'ratings',
      'reactionType',
      'parentComment',
      'country',
      'date',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'originating_language',
      'emotions'
    ]
  },
  sortOptions: [
    {
      label: 'Most Engagements',
      value: 'pdt_movie_comment_feed.engagements desc'
    },
    { label: 'Most Recent', value: 'pdt_movie_comment_feed.date desc' }
  ]
};

const ReactionsCountQuery = {
  id: 'movieReactionsCount',
  meta: {
    fields: [
      'movie',
      'characters',
      'ratings',
      'reactionType',
      'country',
      'date',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'originating_language',
      'emotions'
    ]
  }
};

const ReactionsEmotionQuery = {
  id: 'movieReactionsEmotions',
  meta: {
    fields: [
      'movie',
      'country',
      'date',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'emotions'
    ]
  }
};

const ReactionsCharacterQuery = {
  id: 'movieReactionsCharacters',
  meta: {
    fields: [
      'movie',
      'country',
      'date',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'characters'
    ]
  }
};

const ReactionsRatingQuery = {
  id: 'movieReactionsRating',
  meta: {
    fields: [
      'movie',
      'country',
      'date',
      'age',
      'gender',
      'genre',
      'network',
      'panel',
      'ratings'
    ]
  }
};

const MovieEngagement = ({
  queries: { movieFavorites, movieEmotionIndex, movieEmotionIndexMeta },
  filters,
  movie,
  queryActions: { runFormattedInlineQueriesForPage },
  updateBenchmarkData,
  blocked
}) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const emotionVotesInfo = getEmotionVotes(movieEmotionIndexMeta);

  const updateQueries = () => {
    runFormattedInlineQueriesForPage(
      moviesConfig.MOVIE_REPORT_FILTERS[moviesConfig.MOVIE_ENGAGEMENT_PAGE_ID],
      filters.fields
    );
    updateBenchmarkData(filters.fields, null, true);
  };

  const viewDownload = viewName => (api, type, title) => {
    return api(
      {
        view: viewName,
        filters: get(filters, 'fields', []),
        queryMeta: movieReportEngagement.queries[viewName]
      },
      type,
      title
    );
  };

  useEffect(() => {
    if (blocked) {
      return;
    }

    const values = extractFilterValues(filters.fields);
    const hasNewValues = !isEqual(values, selectedFilters);
    if (hasNewValues) {
      setSelectedFilters(values);
      updateQueries();
    }
  }, [blocked, filters.fields]);

  return (
    <Grid fluid componentClass="section">
      <Row>
        <Col className="tvt-movie-engagement__fan-favorites-wrapper" xs={12}>
          <ComponentHeader title="Fan Favorites" />
          <ModuleExport
            fetchData
            tableDataDownload={viewDownload('movieFavorites')}
            data={() => {}}
            title="Movie Fan Favorites"
            showTitle={movie.name}
            csv
            xls
            png
          />
          <InlineQuery useChildForLoading query={movieFavorites}>
            {query => (
              <SectionedItems
                className="tvt-show-report__fan-favorites"
                hasSeparators={false}
                itemType={ActorItem}
                disableClick
                list={formatActorsFromQuery(query)}
                loading={movieFavorites.loading}
                actorLoader
              />
            )}
          </InlineQuery>
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="tvt-movie-engagement__emotions-wrapper">
          <ComponentHeader
            title="Emotion Index"
            tooltip="Emotions are indexed relative to the performance of movie that have similar genres"
          />

          <div
            className="hidden"
            style={{
              borderRadius: '4px',
              border: '1px solid #bbb',
              backgroundColor: '#e8e8e8',
              padding: '7px 12px',
              marginBottom: '20px'
            }}
          >
            This data is temporarily unavailable as part of a data
            infrastructure migration.
          </div>

          <div className="tvt-movie-engagement-emotion-votes">
            {emotionVotesInfo}
            <ModuleExport
              fetchData
              tableDataDownload={viewDownload('movieEmotionIndex')}
              data={() => {}}
              title="Movie Emotion Index"
              showTitle={movie.name}
              csv
              xls
              png
            />
          </div>
          <InlineQuery useChildForLoading query={movieEmotionIndex}>
            {query => (
              <div className="tvt-movie-engagement-emotion-votes-bar-wrapper">
                <DivergingBar
                  width={1158}
                  axisElement={emotionsAxisElement(query)}
                  query={formatEmotionData(query)}
                />
              </div>
            )}
          </InlineQuery>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="tvt-show-engagement__reactions-col-wrapper">
          <ComponentHeader title="Reactions" />
          <div className="tvt-engagement__reactions-pane">
            <Reactions
              query={ReactionsQuery}
              countQuery={ReactionsCountQuery}
              emotionsQuery={ReactionsEmotionQuery}
              characterQuery={ReactionsCharacterQuery}
              ratingsQuery={ReactionsRatingQuery}
              filters={filters}
              contentType="movie"
              titleName={movie.name}
            />
          </div>
        </Col>
      </Row>
    </Grid>
  );
};

MovieEngagement.propTypes = {
  movie: PropTypes.shape({
    name: PropTypes.string.isRequired
  }).isRequired,
  blocked: PropTypes.bool.isRequired,
  updateBenchmarkData: PropTypes.func.isRequired,
  queries: PropTypes.shape({
    movieCommentFeed: PropTypes.shape({}).isRequired,
    movieFavorites: PropTypes.shape({}).isRequired,
    movieEmotionIndexMeta: PropTypes.shape({}).isRequired,
    movieEmotionIndex: PropTypes.shape({}).isRequired
  }).isRequired,
  looks: PropTypes.shape({
    movieEmotionIndex: PropTypes.shape({}).isRequired
  }).isRequired,
  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,
  filtersActions: PropTypes.shape({
    getFiltersForPage: PropTypes.func.isRequired,
    getFiltersForPageAsync: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired
  }).isRequired,
  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired
};

MovieEngagement.defaultProps = {
  onIframeLoad: () => {}
};

export default MovieEngagement;
